import { Minus, Plus, X } from "lucide-react";
import { BasketItem } from "../../features/orders/itemTypes";

const baseUrl = process.env.REACT_APP_API_IMG_URL

interface ItemServiceSheetProps {
  open: boolean;
  item: BasketItem | null;
  onClose: () => void;
  onChangeQuantity: (serviceId: string, delta: number) => void;
  onConfirm: () => void;
}

export function ItemServiceSheet({
  open,
  item,
  onClose,
  onChangeQuantity,
  onConfirm,
}: ItemServiceSheetProps) {
  if (!open || !item) return null;

  const selectedCount = item.services.reduce(
    (sum, service) => sum + service.qty,
    0,
  );
  const imageValue = item.image.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
      <div className="w-full max-w-md rounded-t-3xl bg-white shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col">
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1.5 w-14 rounded-full bg-gray-300" />
        </div>

        <div className="px-5 pb-4 flex items-start gap-4 border-b border-gray-200">
          <img
            src={`${baseUrl}/inventories/${imageValue}`}
            alt={item.title}
            className="h-24 w-24 rounded-2xl object-cover bg-gray-100 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 leading-tight">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Choose the service and quantity for this item.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500"
                aria-label="Close service sheet"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {item.services.map((service) => (
            <div
              key={service.id}
              className="grid grid-cols-[1fr_auto] items-center gap-4 rounded-2xl bg-white"
            >
              <div>
                <p className="text-lg font-medium text-gray-900">
                  {service.title}
                </p>
                <p className="text-sm text-gray-500 mt-1">{service.price}</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onChangeQuantity(service.id, -1)}
                  className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-700"
                  aria-label={`Decrease ${service.title}`}
                >
                  <Minus className="h-5 w-5" />
                </button>
                <div className="min-w-8 text-center text-lg font-semibold text-gray-900">
                  {service.qty}
                </div>
                <button
                  type="button"
                  onClick={() => onChangeQuantity(service.id, 1)}
                  className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-700"
                  aria-label={`Increase ${service.title}`}
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="p-5 pb-10 border-t border-gray-200">
          <button
            type="button"
            onClick={onConfirm}
            className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white disabled:opacity-50"
            disabled={selectedCount === 0}
          >
            Add to basket
          </button>
        </div>
      </div>
    </div>
  );
}
